import { useCallback, useContext, useEffect, useState } from "react"
import { DateTime } from "luxon"

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"

//Formik helpers
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikTextAreaInput from "../../../helpers/formikInputs/FormikTextAreaInput"
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"
import FormikCalendar from "../../../helpers/formikInputs/FormikCalendar"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//primeReact
import { Dialog } from "primereact/dialog"
import { ConfirmDialog } from "primereact/confirmdialog" // For <ConfirmDialog /> component
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method

//housing API and components
import {
  housingApiCreateActivityPost,
  housingApiEditActivityPost,
} from "../housingApi"
import { LogError } from "../../../helpers/logger"
import HousingActivitiesSubstanceInputs from "./HousingActivitiesSubstanceInputs"
import HousingActivitiesMedicineInputs from "./HousingActivitiesMedicineInputs"

export default function HousingActivitiesForm({
  showForm,
  onHideForm,
  onActivityCreated,
  activityProfile,
  participant,
  houseParticipants,
  activeHouseParticipants,
  activeHouseId,
  houses,
}) {
  const userCtx = useContext(UserContext)

  const getActivity = useCallback(() => {
    let activity = {
      userId: userCtx.session.user.staff_id,
      participantId: participant?.participant_id,
      houseId: activeHouseId,
      organizationId: userCtx.session.user.organization_id,
      activityType: undefined,
      activityTypeOther: "",
      note: "",
      startTime: undefined,
      endTime: undefined,
      medicineManagement: {
        type: "",
        storage: "",
        dispensing: "",
        dispensingFrequency: "",
        dispensingFrequencyOther: "",
      },
      substanceTest: {
        type: "",
        typeOther: "",
        result: "",
        resultSubstance: [],
        outcome: "",
        outcomeOther: "",
      },
    }

    return activity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeHouseId,
    userCtx.session.user.organization_id,
    participant?.participant_id,
    userCtx.session.user.staff_id,
  ])

  const [activity, setActivity] = useState(getActivity())
  const [filterHouse, setFilterHouse] = useState(houses)

  useEffect(() => {
    let activity = getActivity()

    if (!!activityProfile) {
      activity = {
        ...activityProfile.activity,
        activityTypeOther: activityProfile.other?.otherActivity,
        note: activityProfile.activity.notes,
        medicineManagement: !!activityProfile.medicationManagement
          ? activityProfile.medicationManagement
          : activity.medicineManagement,
        substanceTest: !!activityProfile.substanceTest
          ? activityProfile.substanceTest
          : activity.substanceTest,
      }

      activity.startTime = !!activity.startTime
        ? DateTime.fromISO(activity.startTime, { zone: "utc" })
            .toLocal()
            .toJSDate()
        : undefined

      activity.endTime = !!activity.endTime
        ? DateTime.fromISO(activity.endTime, { zone: "utc" })
            .toLocal()
            .toJSDate()
        : undefined

      if (!!activityProfile.substanceTestSubstance) {
        activity.substanceTest.resultSubstance =
          activityProfile.substanceTestSubstance.map(
            (substance) => substance.substanceName,
          )
      }

      const filteredHouses = houses.filter((house) =>
        house.participants.some(
          (participant) => participant === activity.participantId,
        ),
      )
      setFilterHouse(filteredHouses)
    }

    setActivity(activity)
  }, [activityProfile, getActivity, houses])

  const validationSchema = Yup.object().shape({
    participantId: Yup.string().required("The participant is a required field"),
    houseId: Yup.string().required("The house is a required field"),
    activityType: Yup.string().required(
      "The activity type is a required field",
    ),
    activityTypeOther: Yup.string().when("activityType", {
      is: "Other",
      then: Yup.string().required(
        "Must specify the activity type value when Other is selected",
      ),
    }),
    startTime: Yup.date().required(
      "Please specify the start time for the activity",
    ),
    endTime: Yup.date()
      .required("Please specify the end time for the activity")
      .min(
        Yup.ref("startTime"),
        "The end time cannot be before the start time",
      ),
    medicineManagement: Yup.object().when("activityType", {
      is: "MedicineManagement",
      then: Yup.object().shape({
        type: Yup.string().required(
          "The type of medicine management is required",
        ),
        storage: Yup.string().required("Medicine storage is a required field"),
        dispensing: Yup.string().required(
          "Medicine dispenser is a required field",
        ),
        dispensingFrequency: Yup.string(),
        dispensingFrequencyOther: Yup.string().when("dispensingFrequency", {
          is: "Other",
          then: Yup.string().required(
            "This field required if the dispensing frequency is set to Other",
          ),
        }),
      }),
    }),
    substanceTest: Yup.object().when("activityType", {
      is: "SubstanceTest",
      then: Yup.object().shape({
        type: Yup.string().required("The type of substance test is required"),
        typeOther: Yup.string().when("type", {
          is: "Other",
          then: Yup.string().required(
            "This field required if the substance test is set to Other",
          ),
        }),
        result: Yup.string().required(
          "The result of the substance test is required",
        ),
        resultSubstance: Yup.array().when("result", {
          is: "positive",
          then: Yup.array().min(
            1,
            "At least 1 substances is required for a positive test",
          ),
        }),
        outcome: Yup.string().when("result", {
          is: "positive",
          then: Yup.string().required("The outcome of the test is required"),
        }),
        outcomeOther: Yup.string().when("outcome", {
          is: "Other",
          then: Yup.string().required(
            "This field required if the test outcome is set to Other",
          ),
        }),
      }),
    }),
  })

  const buildPayload = (values) => {
    let payload = {
      userId: values.userId,
      participantId: values.participantId,
      houseId: values.houseId,
      organizationId: values.organizationId,
      activityType: values.activityType,
      activityTypeOther: values.activityTypeOther,
      note: values.note,
      removalDate: values.removalDate,
      removalUserId: values.removalUserId,
      startTime: !!values.startTime
        ? DateTime.fromJSDate(values.startTime)
            .set({ second: 0 })
            .toUTC()
            .toISO()
        : undefined,
      endTime: !!values.endTime
        ? DateTime.fromJSDate(values.endTime).set({ second: 0 }).toUTC().toISO()
        : undefined,
    }

    if (values.activityType === "MedicineManagement") {
      payload.medicineManagement = values.medicineManagement
    }

    if (values.activityType === "SubstanceTest") {
      payload.substanceTest = values.substanceTest
    }
    return payload
  }

  const submitActivity = async (values, setErrors) => {
    try {
      let hadErrors = false

      if (values.activityType === "SubstanceTest") {
        if (
          values.substanceTest.type === "other" &&
          !values.substanceTest.typeOther
        ) {
          setErrors({
            substanceTest: {
              typeOther:
                "A Test type description is required for Other test types",
            },
          })
          hadErrors = true
        }

        if (
          values.substanceTest.outcome === "other" &&
          !values.substanceTest.outcomeOther
        ) {
          setErrors({
            substanceTest: {
              outcomeOther:
                "An outcome description is required for Other outcome results. ",
            },
          })
          hadErrors = true
        }
      }

      if (!hadErrors) {
        const payload = buildPayload(values)

        let result = undefined
        if (!!values.id) {
          payload.id = values.id
          result = await housingApiEditActivityPost(
            userCtx.tokens.getHousingToken(),
            payload,
          )
        } else {
          result = await housingApiCreateActivityPost(
            userCtx.tokens.getHousingToken(),
            payload,
          )
        }

        if (!!result) {
          let participant = houseParticipants.find(
            (participant) =>
              participant.participant_id === values.participantId,
          )

          onActivityCreated(participant)
        } else {
          LogError("Unable to save activity", result)
          userCtx.setNotificationError(
            `Unable to save activity: ${result.message}`,
          )
        }
      }

      return !hadErrors
    } catch (error) {
      LogError("Unable to save activity", error)
      userCtx.setNotificationError(`Unable to save activity: ${error.message}`)
      return false
    }
  }

  return (
    <Dialog
      header={`House Activity`}
      visible={showForm}
      onHide={() => {
        setFilterHouse(houses)
        onHideForm()
      }}
      style={{ width: "800px", maxHeight: "900px" }}
    >
      <Formik
        initialValues={activity}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          return submitActivity(values, setErrors)
        }}
      >
        {({ isSubmitting, values, dirty, handleSubmit, setFieldValue }) => (
          <Form>
            <ConfirmDialog />
            <FormikDropDown
              disabled={isSubmitting || !!values.id}
              name="participantId"
              label={"Participant"}
              options={
                !!values.id && !!houseParticipants
                  ? houseParticipants
                  : activeHouseParticipants
              }
              required={true}
              optionLabel={"name2"}
              optionValue={"participant_id"}
              placeHolderValue={"Please select a participant"}
              onChangeCallback={(value) => {
                const filteredHouses = houses.filter((house) =>
                  house.activeParticipants.some(
                    (participant) => participant === value,
                  ),
                )
                setFilterHouse(filteredHouses)

                if (
                  !filteredHouses.some((house) => house.id === value.houseId)
                ) {
                  setFieldValue("houseId", undefined)
                }
              }}
            />
            <FormikDropDown
              disabled={isSubmitting || !!values.id}
              name="houseId"
              label={"House"}
              options={filterHouse}
              required={true}
              optionLabel={"name"}
              optionValue={"id"}
              placeHolderValue={"Please select a house"}
            />
            <FormikCalendar
              disabled={isSubmitting}
              name="startTime"
              label={"Start time"}
              showTime={true}
            />
            <FormikCalendar
              disabled={isSubmitting}
              name="endTime"
              label={"End time"}
              showTime={true}
              minDate={values.startTime}
            />

            <FormikDropDown
              disabled={isSubmitting}
              name="activityType"
              label={"Activity type"}
              options={[
                { label: "Medication Management", value: "MedicineManagement" },
                { label: "Substance Use Testing", value: "SubstanceTest" },
                { label: "Other Housing Activity", value: "Other" },
              ]}
              placeHolderValue={"Please select an activity type"}
              required={true}
            />
            {values.activityType === "Other" ? (
              <FormikTextInput
                disabled={isSubmitting}
                name={"activityTypeOther"}
                label={"Provide other activity description"}
                placeholderText={"Provide other activity description"}
                required={true}
              />
            ) : null}

            <FormikTextAreaInput
              disabled={isSubmitting}
              name={"note"}
              label={"Notes"}
              rows={5}
            />
            <HousingActivitiesMedicineInputs
              values={values}
              disabled={isSubmitting}
            />
            <HousingActivitiesSubstanceInputs
              values={values}
              disabled={isSubmitting}
            />
            <div style={{ float: "right" }}>
              <button
                type="submit"
                className="btn"
                disabled={isSubmitting || !dirty}
              >
                Save
              </button>
              {!!values.id ? (
                <button
                  type="button"
                  className="btn"
                  style={{ marginLeft: "10px" }}
                  disabled={isSubmitting}
                  onClick={() => {
                    confirmDialog({
                      message: "Are you sure you want to proceed?",
                      header: "Confirmation",
                      icon: "pi pi-exclamation-triangle",
                      accept: () => {
                        values.removalDate = DateTime.now().toUTC().toISO()
                        values.removalUserId = userCtx.session.user.staff_id
                        handleSubmit()
                      },
                    })
                  }}
                >
                  Delete
                </button>
              ) : null}

              <button
                type="button"
                className="btn"
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  onHideForm()
                  setFilterHouse(houses)
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
